/**
 * CRM-QUOTE-PIPELINE — pure signal helpers for the Quote Pipeline card face.
 *
 * A quote card should tell the operator, at a glance, whether it needs
 * attention. Two derived signals power that:
 *
 *   daysInLane(lane_entered_at)      → integer age the card has sat in its
 *                                      current lane (null when unknown).
 *   quoteChaseLevel(laneKind, age)   → 'chase' | 'stale' | 'none'
 *
 * "chase" = an actively-progressing quote (Quoting / Submitted) that has gone
 * silent past CHASE_DAYS — the operator should follow up NOW. "stale" = any
 * still-open quote sat past STALE_DAYS — softer nudge. Terminal lanes
 * (Awarded / Lost) never raise a signal: the deal is done either way.
 *
 * All pure I→O so the card's nudge logic is unit-tested without mounting the
 * tile (and so the thresholds are debuggable in one place).
 */

/** Active, working quote lanes — a still-open deal can be chased here. */
export const QUOTE_ACTIVE_LANES: ReadonlySet<string> = new Set([
  'QUOTE_OPEN',
  'QUOTE_QUOTING',
  'QUOTE_SUBMITTED',
])

/** Lanes where silence is actionable — the quote is in the buyer's court. */
export const QUOTE_CHASE_LANES: ReadonlySet<string> = new Set([
  'QUOTE_QUOTING',
  'QUOTE_SUBMITTED',
])

/** Days a Quoting/Submitted quote may sit silent before it needs chasing. */
export const QUOTE_CHASE_DAYS = 7
/** Days any open quote may sit before it reads as stale. */
export const QUOTE_STALE_DAYS = 21

/** Whole days the card has sat in its current lane, or null when the server
 *  sent no `lane_entered_at` (older BE bundle) or an unparseable value.
 *  A future / clock-skewed timestamp clamps to 0 rather than going negative. */
export function daysInLane(
  laneEnteredAt: string | null | undefined,
  now: Date = new Date(),
): number | null {
  if (!laneEnteredAt) return null
  const t = Date.parse(laneEnteredAt)
  if (Number.isNaN(t)) return null
  const ms = now.getTime() - t
  if (ms <= 0) return 0
  return Math.floor(ms / 86_400_000)
}

export type QuoteChaseLevel = 'none' | 'stale' | 'chase'

/** Decide the card's nudge level from its lane + age-in-lane. Chase (the
 *  stronger call to action) wins over stale; terminal/unknown lanes and
 *  unknown ages are always 'none'. */
export function quoteChaseLevel(
  laneKind: string | undefined,
  ageDays: number | null,
): QuoteChaseLevel {
  if (ageDays == null || !laneKind || !QUOTE_ACTIVE_LANES.has(laneKind)) return 'none'
  if (QUOTE_CHASE_LANES.has(laneKind) && ageDays >= QUOTE_CHASE_DAYS) return 'chase'
  if (ageDays >= QUOTE_STALE_DAYS) return 'stale'
  return 'none'
}
